import {
  Add as AddIcon,
  Delete,
  Edit,
  ToggleOff,
  ToggleOn,
  Upload as UploadIcon,
} from "@mui/icons-material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingSpinner from "./../../components/common/LoadingSpinner";
import NotificationBanner from "./../../components/common/NotificationBanner";
import DeleteConfirmationDialog from "./../../components/DeleteConfirmationDialog";
import NewUserForm from "./../../components/NewUserForm";
import { apiService } from "./../../services/api/ApiService";
import { UserProfile } from "./../../services/api/type";

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  // const [confirmName, setConfirmName] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState("");
  // const [newPassword, setNewPassword] = useState("");
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  const [roles, setRoles] = useState<
    { id: number; name: string; description: string }[]
  >([]);
  const [selectedRole, setSelectedRole] = useState<number | string>("");
  // Nuevo estado para controlar la actualización
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    type: "success" as "success" | "error" | "warning" | "info",
    title: "",
    message: "",
  });
  // Estados para carga masiva
  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState<{
    success: number;
    errors: number;
    errorDetails: string[];
  } | null>(null);

  // const handleDeletePermission = (permissionToDelete) => {
  //   setPermissions((prev) =>
  //     prev.filter((perm) => perm !== permissionToDelete)
  //   );
  // };

  // Extrae la lógica de fetchUsers a una función reutilizable
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error("Token not available");
      }

      // Obtener y parsear el perfil del usuario desde el localStorage
      const userProfile = localStorage.getItem("userProfile");
      if (!userProfile) {
        throw new Error("User profile not found in localStorage");
      }
      const parsedUserProfile = JSON.parse(userProfile);
      const loggedUserId = parsedUserProfile.id;

      // Obtener la lista de usuarios desde la API
      const usersData = await apiService.getUsers();

      // Filtrar al usuario logueado de la lista
      const filteredData = usersData.filter(
        (user: UserProfile) => user.id !== loggedUserId
      );

      // Ordenar por fecha de creación (más reciente primero)
      const sortedData = filteredData.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Orden descendente (más reciente primero)
      });

      // Actualizar el estado con los datos filtrados y ordenados
      setUsers(sortedData);
      setFilteredUsers(sortedData);
    } catch (error) {
      console.error("Error fetching roles:", error);

      // Type-safe error handling
      if (
        error instanceof Error &&
        error.message === 'API Error: 403 - {"message":"Forbidden"}'
      ) {
        // Redirigir al login si se recibe un 403 Forbidden
        localStorage.clear();
        window.location.href = "/session-closed";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await apiService.getAdminRoles();
        if (Array.isArray(rolesData)) {
          // Filtra los roles para incluir solo "Manager" y "User"
          const filteredRoles = rolesData.filter(
            (role: any) =>
              role.name === "Manager" ||
              role.name === "User" ||
              role.name === "Supervisor"
          );

          // Transforma los roles filtrados
          const transformedRoles = filteredRoles.map((role: any) => ({
            id: role.id || role.roleId || Math.random(), // Usa roleId si existe
            name: role.name || "Unknown Role",
            description: role.description || "No description available",
          }));

          setRoles(transformedRoles);
        } else {
          console.error("Roles data is not in the expected format", rolesData);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);

        // Type-safe error handling
        if (
          error instanceof Error &&
          error.message === 'API Error: 403 - {"message":"Forbidden"}'
        ) {
          // Redirigir al login si se recibe un 403 Forbidden
          localStorage.clear();
          window.location.href = "/session-closed";
        }
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        (user.name?.toLowerCase() || "").includes(searchLower) ||
        (user.email?.toLowerCase() || "").includes(searchLower) ||
        (user.role?.toLowerCase() || "").includes(searchLower)
    );
    
    // Mantener el ordenamiento por fecha de creación después del filtrado
    const sortedFiltered = filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA; // Orden descendente (más reciente primero)
    });
    
    setFilteredUsers(sortedFiltered);
    setPage(1);
  }, [searchQuery, users]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };



  const handleOpenNewUserModal = () => {
    setOpenNewUserModal(true);
  };

  const handleCloseNewUserModal = () => {
    setOpenNewUserModal(false);
  };

  const toggleUserStatus = async (id: number) => {
    const user = filteredUsers.find((u) => u.id === id);
    if (user) {
      const newStatus =
        user.status === "ACTIVE" ? "AWAITING_APPROVAL" : "ACTIVE";
      try {
        let token = localStorage.getItem("token");
        if (!token) {
          token = sessionStorage.getItem("token");
        }
        if (!token) {
          throw new Error("Token not available");
        }
        await apiService.updateUserStatus(id, newStatus);

        // Refrescar la lista completa después de actualizar el estado
        await fetchUsers();
      } catch (error) {
        console.error("Error updating user status:", error);

        // Type guard to check if it's an Error with a message
        if (
          error instanceof Error &&
          error.message === 'API Error: 403 - {"message":"Forbidden"}'
        ) {
          // Redirigir al login si se recibe un 403 Forbidden
          localStorage.clear();
          window.location.href = "/session-closed";
        }
      }
    }
  };

  const getStatusColor = (
    status:
      | "ACTIVE"
      | "AWAITING_APPROVAL"
      | "APPROVED"
      | "REJECTED"
      | "DELETED"
      | null
  ) => {
    if (status === null) return "default";
    switch (status) {
      case "ACTIVE":
        return "success";
      case "AWAITING_APPROVAL":
        return "warning";
      case "APPROVED":
        return "info";
      case "REJECTED":
        return "error";
      case "DELETED":
        return "secondary"; // O el color que prefieras para mostrar usuarios eliminados
      default:
        return "default";
    }
  };

  const handleOpenDeleteModal = (user: UserProfile) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };
  const handleCloseDeleteDialog = () => {
    setUserToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      // Llamada a la API para eliminar el usuario
      let token = localStorage.getItem("token");
      if (!token) {
        token = sessionStorage.getItem("token");
      }
      if (!token) {
        throw new Error("Token not available");
      }

      // Llamada a la API de eliminación
      await apiService.deleteUser(userToDelete!.id);

      // Refrescar la lista completa después de eliminar
      await fetchUsers();

      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleOpenEditModal = (user: UserProfile) => {
    setEditingUser(user);
    setUsername(user.username || "");
    setEmail(user.email || "");
    setName(user.name || "");
    setLastName(user.lastName || "");
    setAvatar(user.avatar || "");
    setAvatarPreview(user.avatar || "");
    setSelectedRole(user.role); // Assuming 'roleId' exists in the UserProfile type
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingUser(null);
    // Limpiar los campos del formulario
    setUsername("");
    setEmail("");
    setName("");
    setLastName("");
    setAvatar("");
    setAvatarPreview("");
    setSelectedRole("");
  };

  const handleSubmitEdit = async () => {
    if (editingUser) {
      try {
        setIsUpdating(true);
        let token = localStorage.getItem("token");
        if (!token) {
          token = sessionStorage.getItem("token");
        }
        if (!token) {
          throw new Error("Token not available");
        }

        // Actualizar información del usuario
        await apiService.updateUser(editingUser.id, {
          username,
          email,
          name,
          lastName,
          ...(avatar && { avatar }),
        });

        // Actualizar rol si se seleccionó uno
        const roleObject = roles.find((role) => role.name === selectedRole);
        if (roleObject) {
          const role_id = roleObject.id;
          await apiService.updateUserRole(editingUser.id, role_id);
        }

        // Refrescar la lista completa después de editar
        await fetchUsers();

        handleCloseEditModal();
      } catch (error) {
        console.error("Error updating user:", error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateNewUser = async (newUser: { username: string; email: string; name: string; lastName: string; avatar: string }) => {
    try {
      let token = localStorage.getItem("token");
      if (!token) {
        token = sessionStorage.getItem("token");
      }
      if (!token) {
        throw new Error("Token not available");
      }
      await apiService.addUser(newUser);

      setNotification({
        open: true,
        type: "success",
        title: "Usuario creado",
        message: "El usuario se creó correctamente.",
      });

      // Refrescar la lista completa después de crear el usuario
      await fetchUsers();

      // Limpiar filtros y resetear página
      setSearchQuery("");
      setPage(1);

      // Cerrar el modal después de notificar
      handleCloseNewUserModal();
    } catch (error) {
      console.error("Error creating user:", error);
      setNotification({
        open: true,
        type: "error",
        title: "Error",
        message: "No se pudo crear el usuario.",
      });
    }
  };

  // Funciones para carga masiva
  const handleOpenBulkUploadModal = () => {
    setOpenBulkUploadModal(true);
    setCsvFile(null);
    setBulkUploadProgress(0);
    setBulkUploadResults(null);
  };

  const handleCloseBulkUploadModal = () => {
    setOpenBulkUploadModal(false);
    setCsvFile(null);
    setBulkUploadProgress(0);
    setBulkUploadResults(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setCsvFile(file);
      } else {
        setNotification({
          open: true,
          type: "error",
          title: "Error",
          message: "Por favor, selecciona un archivo CSV válido.",
        });
      }
    }
  };

  const parseCSV = (csvText: string): Array<{ email: string; name: string; lastName: string }> => {
    const lines = csvText.split("\n").filter((line) => line.trim() !== "");
    const users: Array<{ email: string; name: string; lastName: string }> = [];

    // Saltar la primera línea si es el encabezado
    const startIndex = lines[0]?.toLowerCase().includes("email") ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Manejar comillas y separadores
      const values: string[] = [];
      let currentValue = "";
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === "," && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = "";
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());

      // Esperamos: email, name, lastName (o email, nombre, apellido)
      if (values.length >= 3) {
        const email = values[0].replace(/^"|"$/g, "").trim();
        const name = values[1].replace(/^"|"$/g, "").trim();
        const lastName = values[2].replace(/^"|"$/g, "").trim();

        if (email && name && lastName) {
          // Validar formato de email básico
          if (email.includes("@") && email.includes(".")) {
            users.push({ email, name, lastName });
          }
        }
      }
    }

    return users;
  };

  const handleBulkUpload = async () => {
    if (!csvFile) {
      setNotification({
        open: true,
        type: "error",
        title: "Error",
        message: "Por favor, selecciona un archivo CSV.",
      });
      return;
    }

    setIsBulkUploading(true);
    setBulkUploadProgress(0);
    setBulkUploadResults(null);

    try {
      const fileText = await csvFile.text();
      const usersToCreate = parseCSV(fileText);

      if (usersToCreate.length === 0) {
        setNotification({
          open: true,
          type: "error",
          title: "Error",
          message: "No se encontraron usuarios válidos en el archivo CSV.",
        });
        setIsBulkUploading(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errorDetails: string[] = [];

      for (let i = 0; i < usersToCreate.length; i++) {
        const user = usersToCreate[i];
        try {
          // Crear el usuario
          const createdUser = await apiService.addUser({
            username: user.email,
            email: user.email,
            name: user.name,
            lastName: user.lastName,
            avatar: "default-avatar-url",
          });
          
          // Actualizar el estado del usuario a ACTIVE
          if (createdUser && createdUser.id) {
            await apiService.updateUserStatus(createdUser.id, "ACTIVE");
          }
          
          successCount++;
        } catch (error) {
          errorCount++;
          const errorMessage = error instanceof Error ? error.message : "Error desconocido";
          errorDetails.push(`${user.email}: ${errorMessage}`);
        }

        // Actualizar progreso
        const progress = ((i + 1) / usersToCreate.length) * 100;
        setBulkUploadProgress(progress);
      }

      setBulkUploadResults({
        success: successCount,
        errors: errorCount,
        errorDetails,
      });

      // Refrescar la lista de usuarios
      await fetchUsers();

      // Mostrar notificación
      if (errorCount === 0) {
        setNotification({
          open: true,
          type: "success",
          title: "Carga masiva completada",
          message: `Se crearon ${successCount} usuarios correctamente.`,
        });
      } else {
        setNotification({
          open: true,
          type: "warning",
          title: "Carga masiva completada con errores",
          message: `Se crearon ${successCount} usuarios. ${errorCount} usuarios tuvieron errores.`,
        });
      }

      // Limpiar filtros y resetear página
      setSearchQuery("");
      setPage(1);
    } catch (error) {
      console.error("Error processing CSV:", error);
      setNotification({
        open: true,
        type: "error",
        title: "Error",
        message: "Error al procesar el archivo CSV.",
      });
    } finally {
      setIsBulkUploading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Cargando Usuarios" />;

  // Datos para paginación
  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const rangeStart = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * rowsPerPage, total);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Buscar por nombre, email, rol, etc."
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ maxWidth: "776px", mr: 2 }}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#002E74",
              color: "#fff",
              fontSize: "14px",
              height: "40px",
            }}
            startIcon={<UploadIcon />}
            onClick={handleOpenBulkUploadModal}
          >
            Carga masiva
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#002E74",
              color: "#fff",
              fontSize: "14px",
              height: "40px",
            }}
            startIcon={<AddIcon />}
            onClick={handleOpenNewUserModal}
          >
            Nuevo usuario
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="user table">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers
              .slice((page - 1) * rowsPerPage, page * rowsPerPage)
              .map((user) => (
                <TableRow key={user.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar src={user.avatar || ""} sx={{ mr: 2 }} />
                      {user.name + " " + user.lastName}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.status === "ACTIVE" ? "Activo" : "Inactivo"}
                      color={getStatusColor(user.status)}
                      icon={
                        user.status === "ACTIVE" ? <ToggleOn /> : <ToggleOff />
                      }
                      onClick={() => toggleUserStatus(user.id)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditModal(user)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteModal(user)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer de paginación (estilo Comments) */}
      {total > 0 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            select
            label="Filas por página"
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            variant="outlined"
            size="small"
            sx={{ width: 160 }}
          >
            {[5, 10, 20, 30].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            variant="outlined"
            shape="rounded"
          />

          <Typography color="textSecondary">
            {total === 0 ? "0-0" : `${rangeStart}-${rangeEnd}`} de {total}
          </Typography>
        </Box>
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        userName={userToDelete?.name || ""}
        onClose={handleCloseDeleteDialog}
        onDelete={handleConfirmDelete}
      />

      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent
          sx={{
            maxHeight: "60vh", // Define una altura máxima si el contenido es muy grande
            overflowY: "auto", // Habilita el scroll vertical
            "&::-webkit-scrollbar": {
              display: "none", // Oculta la barra de scroll en navegadores WebKit
            },
            "-ms-overflow-style": "none", // Oculta la barra de scroll en IE y Edge
            "scrollbar-width": "none", // Oculta la barra de scroll en Firefox
          }}
        >
          <Box sx={{ padding: 3 }}>
            {/* Avatar Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: 3,
              }}
            >
              <Avatar
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: "4px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #dcdcdc",
                }}
                src={avatarPreview || undefined}
              >
                {avatarPreview ? null : "IMG"}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="avatar-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="avatar-upload">
                <Tooltip title="Subir imagen de perfil">
                  <IconButton color="primary" component="span">
                    <PhotoCamera />
                  </IconButton>
                </Tooltip>
              </label>
            </Box>

            {/* Form Section */}
            <Box
              component="form"
              onSubmit={handleSubmitEdit}
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Grid container spacing={2}>
                {/* Name Input */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    label="Nombre"
                    placeholder="Escribe el nombre aquí"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!name} // Ejemplo de validación simple
                    helperText={!name ? "El nombre es obligatorio" : ""}
                  />
                </Grid>

                {/* Last Name Input */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    label="Apellido"
                    placeholder="Escribe el apellido aquí"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    error={!lastName}
                    helperText={!lastName ? "El apellido es obligatorio" : ""}
                  />
                </Grid>

                {/* Email Input (Disabled) */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Correo Electrónico"
                    value={email}
                    disabled // Campo deshabilitado
                    InputProps={{
                      readOnly: true, // Asegura que sea de solo lectura
                    }}
                    sx={{
                      backgroundColor: "#f9f9f9", // Color para distinguir el campo deshabilitado
                    }}
                  />
                </Grid>
              </Grid>

              {/* Role Selection */}
              <Box>
                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                  Rol Asignado
                </Typography>
                <Select
                  variant="outlined" // Igual que los TextField
                  fullWidth
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: "4px", // Igual que los TextField
                    backgroundColor: "#ffffff", // Igual al fondo de los TextField
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#dcdcdc", // Color de borde similar al de TextField
                      },
                      "&:hover fieldset": {
                        borderColor: "#a3a3a3", // Borde en hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3f51b5", // Borde cuando está enfocado
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Selecciona un rol
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Action Buttons */}
        <DialogActions>
          <Button
            onClick={handleCloseEditModal}
            color="secondary"
            variant="outlined"
            sx={{ marginRight: 2 }}
            disabled={isUpdating}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSubmitEdit}
            disabled={isUpdating}
          >
            {isUpdating ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNewUserModal}
        onClose={handleCloseNewUserModal}
        maxWidth="lg"
      >
        <NewUserForm
          onSubmit={handleCreateNewUser}
          onClose={handleCloseNewUserModal}
        />
      </Dialog>

      {/* Modal de carga masiva */}
      <Dialog
        open={openBulkUploadModal}
        onClose={handleCloseBulkUploadModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Carga masiva de usuarios</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Sube un archivo CSV con el siguiente formato: email, nombre, apellido
            </Typography>
            <Typography variant="caption" sx={{ mb: 3, display: "block", color: "text.secondary" }}>
              Ejemplo:
              <br />
              email,nombre,apellido
              <br />
              usuario1@example.com,Juan,Pérez
              <br />
              usuario2@example.com,María,García
            </Typography>

            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv"
                style={{ display: "none" }}
                id="csv-upload"
                type="file"
                onChange={handleFileChange}
                disabled={isBulkUploading}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={isBulkUploading}
                  sx={{ mb: 2 }}
                >
                  Seleccionar archivo CSV
                </Button>
              </label>
              {csvFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "success.main" }}>
                  Archivo seleccionado: {csvFile.name}
                </Typography>
              )}
            </Box>

            {isBulkUploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Procesando usuarios... {Math.round(bulkUploadProgress)}%
                </Typography>
                <LinearProgress variant="determinate" value={bulkUploadProgress} />
              </Box>
            )}

            {bulkUploadResults && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: bulkUploadResults.errors > 0 ? "#fff3cd" : "#d4edda",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Resultados de la carga:
                </Typography>
                <Typography variant="body2" sx={{ color: "success.main", mb: 1 }}>
                  ✓ Usuarios creados exitosamente: {bulkUploadResults.success}
                </Typography>
                {bulkUploadResults.errors > 0 && (
                  <>
                    <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                      ✗ Errores: {bulkUploadResults.errors}
                    </Typography>
                    {bulkUploadResults.errorDetails.length > 0 && (
                      <Box sx={{ mt: 1, maxHeight: 150, overflow: "auto" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          Detalles de errores:
                        </Typography>
                        {bulkUploadResults.errorDetails.slice(0, 10).map((detail, index) => (
                          <Typography key={index} variant="caption" sx={{ display: "block", color: "error.main" }}>
                            • {detail}
                          </Typography>
                        ))}
                        {bulkUploadResults.errorDetails.length > 10 && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            ... y {bulkUploadResults.errorDetails.length - 10} errores más
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseBulkUploadModal}
            color="secondary"
            variant="outlined"
            disabled={isBulkUploading}
          >
            {bulkUploadResults ? "Cerrar" : "Cancelar"}
          </Button>
          {!bulkUploadResults && (
            <Button
              onClick={handleBulkUpload}
              color="primary"
              variant="contained"
              disabled={!csvFile || isBulkUploading}
              startIcon={isBulkUploading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {isBulkUploading ? "Procesando..." : "Cargar usuarios"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <NotificationBanner
        type={notification.type}
        title={notification.title}
        message={notification.message}
        open={notification.open}
        onClose={() => setNotification((p) => ({ ...p, open: false }))}
      />
    </Box>
  );
};

export default UserManagement;
